import { Suspense, useState } from 'react';
import Segment from 'components/Segment';
import Breadcrumbs from 'components/Breadcrumbs';
import dev from 'assets/icons/dev.png';
import duck from 'assets/icons/duck.svg';
import entur from 'assets/icons/entur.png';
import { SearchNavigationProvider } from 'hooks/context/useSearchNavigation';
import staging from 'assets/icons/staging.png';
import AsyncResults from 'components/AsyncResults/AsyncResults';
import CreateNew from 'components/CreateNew/CreateNew';
import { AuthenticationProvider } from 'hooks/context/useAuthContext';
import { DrawerProvider } from 'hooks/context/useDrawer';
import { SkipToContent } from '@entur/a11y';
import { ToastProvider } from '@entur/alert';
import CustomDrawer from 'components/CustomDrawer/index';
import { AsyncResultProvider } from 'hooks/context/useAsyncResults';
import { UserSettingsProvider } from 'hooks/context/useUserSettings';
import AllRoutes from './routes';
import NavBar from './components/NavBar';
import styles from './styles.module.scss';

import { ChartOfAccountsProvider } from 'hooks/context/useChartOfAccounts';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Loader } from '@entur/loader';
import { useIntl } from 'react-intl';
import { DEPLOYMENT } from 'config';
import { OperatingMessagesBanner } from 'features/operating-messages/components/OperatingMessagesBanner';
import { HotkeysProvider } from 'react-hotkeys-hook';

const queryClient = new QueryClient({ defaultOptions: { queries: { retry: false } } });

const AppRoot = () => {
  const intl = useIntl();

  const isLocalhost = window.location.hostname === 'localhost';
  const icon = isLocalhost
    ? duck
    : DEPLOYMENT === 'development' || DEPLOYMENT === 'dev'
      ? dev
      : DEPLOYMENT === 'staging'
        ? staging
        : entur;
  const iconType = isLocalhost ? 'image/svg+xml' : 'image/png';

  const [collapsed, isCollapsed] = useState(false);

  return (
    <>
      <link rel="icon" type={iconType} href={icon} sizes="16x16" />
      <QueryClientProvider client={queryClient}>
        {DEPLOYMENT === 'development' && <ReactQueryDevtools />}
        <HotkeysProvider>
          <ToastProvider>
            <AuthenticationProvider>
              <DrawerProvider>
                <ChartOfAccountsProvider>
                  <UserSettingsProvider>
                    <SkipToContent mainId="main-content">
                      {intl.formatMessage({ id: 'a11y.skipToContent' })}
                    </SkipToContent>
                    <NavBar collapsed={collapsed} updateCollapsed={(bool) => isCollapsed(bool)} />
                    <Segment collapsed={collapsed}>
                      <OperatingMessagesBanner />
                      <div className={styles.topHeader}>
                        <Breadcrumbs />
                        <CreateNew />
                      </div>
                      <SearchNavigationProvider>
                        <AsyncResultProvider>
                          <AsyncResults />
                          <CustomDrawer />
                          <Suspense fallback={<Loader>{intl.formatMessage({ id: 'generalValues.loading' })}</Loader>}>
                            <AllRoutes />
                          </Suspense>
                        </AsyncResultProvider>
                      </SearchNavigationProvider>
                    </Segment>
                  </UserSettingsProvider>
                </ChartOfAccountsProvider>
              </DrawerProvider>
            </AuthenticationProvider>
          </ToastProvider>
        </HotkeysProvider>
      </QueryClientProvider>
    </>
  );
};

export default AppRoot;
